import { locationValues } from "@geomatico/maplibre-cog-protocol";
import { skipToken, useQueries, useQuery } from "@tanstack/react-query";

import { getRanaApiV1 } from "@nens/rana/api-v1";

/**
 * What this does:
 * This query estimates a good color range for non-DEM COG rasters, so they render with useful
 * colors instead of appearing very dark, flat, or fully black.
 *
 * How it works:
 * - It samples pixel values across the visible bounds using `locationValues(...)`.
 * - It uses the 5th and 95th percentile as min/max, which avoids extreme outliers.
 * - It skips DEM rasters, because DEMs are rendered as hillshade (`raster-dem`) and do not use
 *   this color-range flow.
 *
 * Source:
 * - https://github.com/geomatico/maplibre-cog-protocol
 * - Local dependency docs:
 *   node_modules/@geomatico/maplibre-cog-protocol/README.md
 */

type CogBounds = [number, number, number, number];

type CogColorRangeQueryArgs = {
  rasterId: number | undefined;
  rasterUrl: string | undefined;
  bounds: CogBounds | undefined;
};

const DEFAULT_GRID_SIZE = 7;

function percentile(sortedValues: number[], p: number) {
  const index = (sortedValues.length - 1) * p;
  const low = Math.floor(index);
  const high = Math.ceil(index);

  if (low === high) return sortedValues[low];

  const fraction = index - low;
  return sortedValues[low] * (1 - fraction) + sortedValues[high] * fraction;
}

function buildSampleLocations(bounds: CogBounds, gridSize: number) {
  const [minLng, minLat, maxLng, maxLat] = bounds;
  const samples = Math.max(2, gridSize);

  const points: { latitude: number; longitude: number }[] = [];

  for (let x = 0; x < samples; x++) {
    const xRatio = x / (samples - 1);
    const longitude = minLng + (maxLng - minLng) * xRatio;

    for (let y = 0; y < samples; y++) {
      const yRatio = y / (samples - 1);
      const latitude = minLat + (maxLat - minLat) * yRatio;
      points.push({ latitude, longitude });
    }
  }

  return points;
}

async function getCogColorRange(url: string, bounds: CogBounds, gridSize = DEFAULT_GRID_SIZE) {
  const sampleLocations = buildSampleLocations(bounds, gridSize);

  const values = await Promise.all(
    sampleLocations.map(async (location) => {
      try {
        const sampledValues = await locationValues(url, location);
        return sampledValues[0];
      } catch {
        return Number.NaN;
      }
    })
  );

  const finiteValues = values.filter((value): value is number => Number.isFinite(value));

  if (finiteValues.length < 2) return undefined;

  const sortedValues = [...finiteValues].sort((a, b) => a - b);

  const rangeMin = percentile(sortedValues, 0);
  const rangeMax = percentile(sortedValues, 1);

  if (!Number.isFinite(rangeMin) || !Number.isFinite(rangeMax)) return undefined;

  if (rangeMin >= rangeMax) {
    const absoluteValue = Math.abs(rangeMin);
    const epsilon = Math.max(absoluteValue * 0.01, 1e-6);
    return [rangeMin - epsilon, rangeMax + epsilon] as [number, number];
  }

  return [rangeMin, rangeMax] as [number, number];
}

function getCogColorRangeQuery(args: CogColorRangeQueryArgs) {
  const { rasterId, rasterUrl, bounds } = args;

  return getRanaApiV1().getQueryOptions<[number, number] | undefined>({
    queryKey: ["cog-color-range", args],
    queryFn:
      rasterId && rasterUrl && bounds ? () => getCogColorRange(rasterUrl, bounds) : skipToken,
    staleTime: Infinity,
  });
}

export const useCogColorRangeQuery = (args: CogColorRangeQueryArgs) => {
  return useQuery(getCogColorRangeQuery(args));
};

export const useCogColorRangeQueries = (argsList: CogColorRangeQueryArgs[]) => {
  return useQueries({
    queries: argsList.map((args) => getCogColorRangeQuery(args)),
  });
};
